import { prepareProductImage } from '../../../../helpers/product-image'
import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { DialogService } from '../../../../services/dialog.service'
import { ProductService } from '../../../../services/product.service'
import { RouteService } from '../../../../services/route.service'
import { TranslationService } from '../../../../services/translation.service'
import { ROUTES } from '../../../../constants/routes'
import { MatButtonModule } from '@angular/material/button'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { ProductCsvTemplate } from '../../../../types/csvTemplates'
import { ProductCsvTemplatesService } from '../../../../services/productCsvTemplate.service'
import { firstValueFrom } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'
import { SpinnerService } from '../../../../services/spinner.service'
import { ApiError } from '../../../../types/apiError'
import { ErrorService } from '../../../../services/error.service'
import { ReviewProductsComponent } from './review-products/review-products.component'
import {
  CreateImportedProductsRequest,
  CSVImportResponse,
} from '../../../../types/products'

@Component({
  selector: 'app-import-products',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
    ReviewProductsComponent,
    RouterLink,
  ],
  templateUrl: './import-products.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './import-products.component.scss',
})
export class ImportProductsComponent implements OnInit {
  private fb = inject(FormBuilder)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private routeService = inject(RouteService)
  private productService = inject(ProductService)
  translationService = inject(TranslationService)
  private templateService = inject(ProductCsvTemplatesService)
  private spinnerService = inject(SpinnerService)
  private errorService = inject(ErrorService)

  private destroyRef = inject(DestroyRef)
  @ViewChild('stageHeading') stageHeading?: ElementRef<HTMLElement>
  @ViewChild('uploadForm') uploadForm?: ElementRef<HTMLFormElement>

  stage: 'upload' | 'review' | 'complete' = 'upload'
  private operation: 'validation' | 'confirmation' | 'download' | null = null
  get processing() {
    return this.operation
  }
  set processing(value: 'validation' | 'confirmation' | 'download' | null) {
    this.operation = value
    // Disabling the control must not trigger review invalidation.
    if (value) this.importForm.controls.template.disable({ emitEvent: false })
    else this.importForm.controls.template.enable({ emitEvent: false })
  }
  completedCount = 0
  operationError = ''
  reviewInvalidated = false
  private inputRevision = 0
  private validatedRevision: number | null = null
  fileData: Record<string, File | File[]> = {}
  readonly productsLink = `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`

  get canConfirm(): boolean {
    return (
      this.stage === 'review' &&
      !this.processing &&
      this.validatedRevision === this.inputRevision &&
      this.importResponse.productsToImport.length > 0 &&
      this.importResponse.productsToImport.every(
        (row) => row.validationStatus === 'valid',
      )
    )
  }

  get stageTitle(): string {
    if (this.stage === 'complete')
      return this.translationService.getImportCompleteText()
    if (this.stage === 'review')
      return this.translationService.getImportReviewText()
    return this.translationService.getUploadAndValidateFilesText()
  }

  templates: Array<ProductCsvTemplate> = []

  importForm = this.fb.group({
    template: ['marketplace', Validators.required],
  })

  importResponse: CSVImportResponse = {
    productsToImport: [],
    errorMap: new Map(),
  }

  async ngOnInit() {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)

    this.importForm.controls.template.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.invalidateReview())
    await this.loadTemplates()
  }

  /**
   * Fetches csv templates for selection
   */
  async loadTemplates() {
    try {
      this.spinnerService.startSpinner()
      this.templates = await firstValueFrom(this.templateService.getAll())
      this.spinnerService.stopSpinner()
    } catch {
      this.spinnerService.stopSpinner()
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getSomethingWentWrongText(),
        this.translationService.getErrorOccurredText(),
      )
    }
  }

  /**
   * Handles file change
   * @param {Event} event File change event
   * @param {string} fieldName Name of the file(s)
   */
  onFileChange(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement

    if (this.processing) return
    if (input.files?.length) {
      this.fileData[fieldName] = input.multiple
        ? Array.from(input.files)
        : input.files[0]
    } else {
      delete this.fileData[fieldName]
    }
    this.invalidateReview()
  }

  selectedFiles(fieldName: string): File[] {
    const files = this.fileData[fieldName]
    return files ? (Array.isArray(files) ? files : [files]) : []
  }

  removeFiles(fieldName: string, input: HTMLInputElement): void {
    if (this.processing) return
    delete this.fileData[fieldName]
    input.value = ''
    this.invalidateReview()
  }

  private invalidateReview(): void {
    this.reviewInvalidated ||= this.validatedRevision !== null
    this.inputRevision++
    this.validatedRevision = null
    this.importResponse = { productsToImport: [], errorMap: new Map() }
    this.operationError = ''
    this.stage = 'upload'
  }

  backToUpload(): void {
    if (this.processing) return
    this.stage = 'upload'
    this.operationError = ''
    this.stageHeading?.nativeElement.focus()
  }

  async downloadTemplate(): Promise<void> {
    const id = this.importForm.value.template
    if (!id || this.processing) return
    this.processing = 'download'
    try {
      this.spinnerService.startSpinner()
      await this.templateService.downloadFiles(id, 'products')
    } catch (err) {
      this.errorService.handleErr(
        this.dialogService,
        err as ApiError,
        this.dialog,
      )
    } finally {
      this.processing = null
      this.spinnerService.stopSpinner()
    }
  }

  /**
   * Prepares the selected photos and submits the files for import review.
   * @returns A promise that resolves after submission or a handled error.
   */
  async onSubmit(): Promise<void> {
    if (
      this.processing ||
      this.stage !== 'upload' ||
      this.importForm.invalid ||
      !this.fileData['productImportFile']
    )
      return

    this.processing = 'validation'
    this.operationError = ''
    this.validatedRevision = null
    this.importResponse = { productsToImport: [], errorMap: new Map() }
    const revision = this.inputRevision
    try {
      this.spinnerService.startSpinner()
      const formData = new FormData()
      formData.append('template', this.importForm.controls.template.value!)
      // Resize photos one at a time so a large batch does not fill browser memory.
      for (const fieldName of Object.keys(this.fileData)) {
        for (const file of this.selectedFiles(fieldName)) {
          if (fieldName !== 'productPictures') {
            formData.append(fieldName, file)
            continue
          }
          try {
            const image = await prepareProductImage(file)
            formData.append(fieldName, image, file.name)
          } catch (error) {
            const message =
              error instanceof Error && error.message === 'size'
                ? this.translationService.getImageTooLargeText()
                : this.translationService.getInvalidProductImageText()
            this.operationError = `${file.name}: ${message}`
            return
          }
        }
      }
      const response = await firstValueFrom(
        this.productService.importProductsForReview(formData),
      )
      // Ignore results for inputs that changed during validation.
      if (revision !== this.inputRevision) return
      this.importResponse = {
        productsToImport: response.productsToImport || [],
        errorMap: new Map(Object.entries(response.errorMap || {})),
      }
      this.validatedRevision = revision
      this.reviewInvalidated = false
      this.stage = 'review'
      this.stageHeading?.nativeElement.focus()
    } catch (err) {
      this.operationError =
        this.translationService.getImportValidationFailedText()
      this.errorService.handleErr(
        this.dialogService,
        err as ApiError,
        this.dialog,
      )
    } finally {
      this.processing = null
      this.spinnerService.stopSpinner()
    }
  }

  reset(): void {
    if (this.processing) return
    this.uploadForm?.nativeElement.reset()
    this.importForm.reset({ template: 'marketplace' })
    this.fileData = {}
    this.invalidateReview()
    this.reviewInvalidated = false
    this.completedCount = 0
    this.stageHeading?.nativeElement.focus()
  }

  async importProducts(): Promise<void> {
    if (!this.canConfirm) return
    this.processing = 'confirmation'
    this.operationError = ''
    try {
      this.spinnerService.startSpinner()
      const products = this.importResponse.productsToImport.map(
        (productToImport) => productToImport.product,
      )

      const ingredients = this.importResponse.productsToImport.flatMap(
        (productToImport) => productToImport.ingredients || [],
      )

      const resins = this.importResponse.productsToImport.flatMap(
        (productToImport) => productToImport.resins || [],
      )

      const components = this.importResponse.productsToImport.flatMap(
        (productToImport) => productToImport.components || [],
      )
      const componentGroups = this.importResponse.productsToImport.flatMap(
        (productToImport) => productToImport.componentGroups || [],
      )

      const request: CreateImportedProductsRequest = {
        products,
        ingredients,
        resins,
        components,
        componentGroups,
      }

      await firstValueFrom(this.productService.createImportedProducts(request))
      this.completedCount = products.length
      this.stage = 'complete'
      this.validatedRevision = null
      this.stageHeading?.nativeElement.focus()
    } catch (err) {
      this.operationError =
        this.translationService.getImportConfirmationFailedText()
      const apiErr = err as ApiError
      if (apiErr.name === 'ProductImportValidationFailed' && apiErr.details) {
        this.importResponse.errorMap = new Map(
          Object.entries(apiErr.details).filter(
            (entry): entry is [string, string[]] => Array.isArray(entry[1]),
          ),
        )
        this.importResponse.productsToImport =
          this.importResponse.productsToImport.map((row) => ({
            ...row,
            validationStatus: this.importResponse.errorMap.has(
              row.product.primaryArticleIdentifier,
            )
              ? 'invalid'
              : row.validationStatus,
          }))
        return
      }
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.processing = null
      this.spinnerService.stopSpinner()
    }
  }
}
